import React from "react";
import { X, AlertTriangle, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogPortal, DialogOverlay, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

/**
 * AppModal — canonical modal for the entire app.
 * - Fullscreen on mobile, centered card on desktop
 * - Standard header (icon, title, close)
 * - Sticky footer with max 2 actions
 * - Scrollable content
 *
 * variant: info | form | confirmation | destructive | media | payment | admin
 */
const variantStyles = {
    info: "text-md-text",
    form: "text-md-text",
    confirmation: "text-md-text",
    destructive: "text-md-danger",
    media: "text-md-text",
    payment: "text-md-text",
    admin: "text-md-text",
};

export function AppModal({
    open,
    onOpenChange,
    title,
    subtitle,
    icon = null,
    variant = "info",
    loading = false,
    children,
    primaryAction = null,   // { label, onClick, disabled, variant: 'primary'|'destructive'|'ghost' }
    secondaryAction = null, // same shape
    closeOnOverlayClick = true,
    testId,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogPortal>
                <DialogOverlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
                <DialogContent
                    data-testid={testId || "app-modal"}
                    onInteractOutside={(e) => {
                        if (!closeOnOverlayClick) e.preventDefault();
                    }}
                    className="fixed left-1/2 top-1/2 z-50 grid w-full max-w-lg -translate-x-1/2 -translate-y-1/2 gap-0 border border-md-border bg-md-surface p-0 shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 sm:rounded-2xl max-sm:h-[100dvh] max-sm:max-w-full max-sm:rounded-none"
                >
                    {/* Header */}
                    <div className="flex items-start gap-3 border-b border-md-border px-5 pt-safe pb-4 sm:pt-5">
                        {icon && (
                            <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-md-surface-2 ${variantStyles[variant]}`}>
                                {variant === "destructive" ? <AlertTriangle className="h-5 w-5" /> : icon}
                            </div>
                        )}
                        <div className="min-w-0 flex-1">
                            {title ? (
                                <DialogTitle className="text-lg font-semibold tracking-tight text-md-text">
                                    {title}
                                </DialogTitle>
                            ) : (
                                <VisuallyHidden.Root>
                                    <DialogTitle>Fenêtre modale</DialogTitle>
                                </VisuallyHidden.Root>
                            )}
                            {subtitle && (
                                <DialogDescription className="mt-1 text-sm text-md-text-2">
                                    {subtitle}
                                </DialogDescription>
                            )}
                        </div>
                        <button
                            onClick={() => onOpenChange?.(false)}
                            aria-label="Fermer"
                            data-testid="app-modal-close"
                            className="-mr-2 inline-flex h-8 w-8 items-center justify-center rounded-full text-md-text-2 hover:bg-md-surface-2 hover:text-md-text"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    </div>

                    {/* Body */}
                    <div className="md-scroll relative max-h-[calc(100dvh-12rem)] overflow-y-auto px-5 py-5 sm:max-h-[60vh]">
                        {loading && (
                            <div className="absolute inset-0 z-10 flex items-center justify-center bg-md-surface/70">
                                <Loader2 className="h-6 w-6 animate-spin text-md-text-2" />
                            </div>
                        )}
                        {children}
                    </div>

                    {/* Footer */}
                    {(primaryAction || secondaryAction) && (
                        <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-md-border bg-md-surface px-5 py-4 pb-[max(env(safe-area-inset-bottom),1rem)] sm:flex-row sm:justify-end">
                            {secondaryAction && (
                                <button
                                    type="button"
                                    onClick={secondaryAction.onClick}
                                    disabled={secondaryAction.disabled}
                                    data-testid="app-modal-secondary"
                                    className="inline-flex h-11 items-center justify-center rounded-lg border border-md-border bg-transparent px-5 text-sm font-semibold text-md-text hover:bg-md-surface-2 disabled:opacity-50"
                                >
                                    {secondaryAction.label}
                                </button>
                            )}
                            {primaryAction && (
                                <button
                                    type="button"
                                    onClick={primaryAction.onClick}
                                    disabled={primaryAction.disabled || loading}
                                    data-testid="app-modal-primary"
                                    className={
                                        primaryAction.variant === "destructive"
                                            ? "inline-flex h-11 items-center justify-center rounded-lg bg-md-danger px-5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                                            : "inline-flex h-11 items-center justify-center rounded-lg bg-md-primary px-5 text-sm font-semibold text-md-primary-fg hover:opacity-90 disabled:opacity-50"
                                    }
                                >
                                    {primaryAction.label}
                                </button>
                            )}
                        </div>
                    )}
                </DialogContent>
            </DialogPortal>
        </Dialog>
    );
}

export default AppModal;
